import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { billingSchema } from '../../../schema/billing.schema';
import InputField from '../../../component/form/FormInput';
import InputRadioGroup from '../../../component/form/InputRadioGroup/InputRadioGroup';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { master_data } from '../../../store/action/master';
import { newObjectCreater } from '../../../utils/controller';
import InputSelect from '../../../component/inputSelect/InputSelect';
import { toast } from 'react-toastify';
import { getCities } from '../../../services/vendor';
import { useCheckoutFormApi } from '../CheckoutFormContext';
import { fetchBillingPrefill } from '../../../services/subscription';

export default function BillingForm() {
	const masterData = useSelector((state) => state?.master?.masterData?.data);
	const [cityList, setCityList] = useState([]);
	const formApi = useCheckoutFormApi();

	// newObjectCreater() maps over the full country/state list and returns a
	// brand-new array. Without useMemo that array got a new identity on every
	// render, which re-triggered InputSelect's option-filtering effect for
	// every dropdown on every keystroke.
	const countryList = useMemo(() => newObjectCreater(masterData?.countries || []), [masterData]);
	const StateList = useMemo(() => newObjectCreater(masterData?.states || []), [masterData]);

	const {
		control,
		watch,
		setValue,
		trigger,
		getValues,
		formState: { errors },
	} = useForm({
		resolver: zodResolver(billingSchema),
		// onTouched shows an error once a field has been visited, then keeps it
		// live. 'onChange' re-ran the whole zod schema on literally every
		// keystroke in every field, which is wasted work on a 11-field form.
		mode: 'onTouched',
		reValidateMode: 'onChange',
		defaultValues: {
			name: '',
			phone: '',
			address: '',
			country: '',
			pincode: '',
			city: '',
			state: '',
			gst: false,
			gstin: '',
			paymentType: 'recurring',
			paymentMethod: 'upi',
		},
	});

	const dispatch = useDispatch();

	// Only subscribe to the two fields that actually drive conditional UI here.
	// A bare watch() subscribes to every field and returns a new object each
	// render — that was the root cause of the checkout form feeling sluggish.
	const isGstEnabled = watch('gst');
	const paymentType = watch('paymentType');

	// Publish the imperative API instead of pushing form state through Redux on
	// every change. `trigger`/`getValues` are stable across renders, so
	// OrderSummary reads current values only when the user clicks pay.
	useEffect(() => {
		formApi.current = { trigger, getValues };
	}, [formApi, trigger, getValues]);

	useEffect(() => {
		dispatch(master_data());
	}, [dispatch]);

	// Prefill from the signup profile + organisation setup + any billing
	// details saved on a previous checkout. The form used to open completely
	// empty, so every customer retyped details they had already given us.
	//
	// Runs once, and only fills fields the user has NOT touched: `setValue`
	// with `shouldDirty: false` leaves a field the user has already edited
	// alone if this resolves late. A field with no stored value is skipped
	// entirely rather than written as '' — the ticket requires missing values
	// to stay blank and editable, and writing '' would mark the form dirty for
	// no reason.
	useEffect(() => {
		let cancelled = false;

		fetchBillingPrefill()
			.then((prefill) => {
				if (cancelled || !prefill) return;

				const fill = (fieldName, value) => {
					if (value === null || value === undefined || value === '') return;
					// Never clobber something the user has already typed — this
					// request can resolve after they start filling the form in.
					if (getValues(fieldName)) return;
					setValue(fieldName, value, { shouldDirty: false, shouldValidate: false });
				};

				fill('name', prefill.name);
				fill('phone', prefill.phone);
				fill('address', prefill.address);
				fill('country', prefill.country);
				fill('pincode', prefill.pincode);
				fill('state', prefill.state);
				fill('city', prefill.city);

				// `state_id` is a hidden field the state <select> normally sets as a
				// side effect of onSelect. Prefilling the visible name without it
				// would submit a state with no id.
				fill('state_id', prefill.state_id);

				// GST mode is only switched on when we actually hold a number —
				// see the note in billingPrefill.js. Turning it on without one
				// would make GSTIN required and immediately invalid.
				if (prefill.gstin) {
					fill('gstin', prefill.gstin);
					setValue('gst', true, { shouldDirty: false, shouldValidate: false });
				}

				// The city list is keyed off the selected state, so it has to be
				// loaded for the prefilled city to be selectable at all.
				if (prefill.state_id) getCity(prefill.state_id);
			})
			.catch((err) => {
				// Non-fatal by design: an empty form the customer can fill in is a
				// far better outcome than blocking checkout because a convenience
				// lookup failed.
				console.error('Billing prefill failed:', err);
			});

		return () => {
			cancelled = true;
		};
		// Intentionally once on mount — re-running would fight the user's edits.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	async function getCity(stateId) {
		try {
			const res = await getCities({ state_id: Number(stateId) });

			if (res?.success) {
				setCityList(newObjectCreater(res?.data));
			}
		} catch (error) {
			toast.error(error?.message ?? 'Failed to load cities');
		}
	}

	return (
		<form>
			<div className="card-custom">
				<h5 className="card-title mb-3">Billing Details</h5>
				<p className="card-text">Provide your details for billing and invoices.</p>

				<div className="row g-3">
					<div className="col-md-6">
						<Controller
							name="name"
							control={control}
							render={({ field }) => (
								<InputField
									{...field}
									label="Billing Name/Company Name"
									placeholder="Billing Name"
									required
									error={errors.name?.message}
								/>
							)}
						/>
					</div>

					<div className="col-md-6">
						<Controller
							name="phone"
							control={control}
							render={({ field }) => (
								<InputField
									{...field}
									label="Phone Number"
									placeholder="Phone Number"
									required
									error={errors.phone?.message}
								/>
							)}
						/>
					</div>

					<div className="col-12">
						<Controller
							name="address"
							control={control}
							render={({ field }) => (
								<InputField
									{...field}
									label="Street Address"
									placeholder="Street Address"
									required
									error={errors.address?.message}
								/>
							)}
						/>
					</div>

					<div className="col-md-6">
						<Controller
							name="country"
							control={control}
							render={({ field }) => (
								<InputSelect
									label="Country"
									placeholder="Select Country"
									options={countryList}
									value={field.value}
									required
									onSelect={(option) => {
										field.onChange(option.value);
									}}
									onChange={() => {}}
									error={errors.country?.message}
								/>
							)}
						/>
					</div>

					<div className="col-md-6">
						<Controller
							name="pincode"
							control={control}
							render={({ field }) => (
								<InputField
									{...field}
									label="Pin Code"
									placeholder="Pin Code"
									required
									error={errors.pincode?.message}
								/>
							)}
						/>
					</div>

					<div className="col-md-6">
						<Controller
							name="state"
							control={control}
							render={({ field }) => (
								<InputSelect
									label="State"
									placeholder="Select State"
									options={StateList}
									value={field.value}
									required
									onSelect={(option) => {
										if (!option) return;
										field.onChange(option.value);
										setValue('state_id', option.value);
										getCity(option.key);
									}}
									onChange={() => {}}
									error={errors.state?.message}
								/>
							)}
						/>
					</div>

					<div className="col-md-6">
						<Controller
							name="city"
							control={control}
							render={({ field }) => (
								<InputSelect
									label="City"
									placeholder="Select City"
									options={cityList}
									value={field.value}
									required
									onSelect={(option) => {
										field.onChange(option.value);
									}}
									onChange={() => {}}
									error={errors.city?.message}
								/>
							)}
						/>
					</div>

					<div className="col-12 mt-2">
						<Controller
							name="gst"
							control={control}
							render={({ field }) => (
								<div className="form-check">
									<input
										type="checkbox"
										checked={field.value}
										onChange={(e) => field.onChange(e.target.checked)}
										className="form-check-input"
										id="gst"
									/>
									<label
										htmlFor="gst"
										className="form-check-label"
									>
										GST Invoice Required
									</label>
								</div>
							)}
						/>
					</div>
					{isGstEnabled && (
						<div className="col-6 mt-2">
							<Controller
								name="gstin"
								control={control}
								render={({ field }) => (
									<InputField
										{...field}
										label="GSTIN"
										onChange={(value) => {
											field.onChange(value.replace(/\s/g, '').toUpperCase());
										}}
										maxLength={15}
										placeholder="Enter GSTIN"
										required
										error={errors.gstin?.message}
									/>
								)}
							/>
						</div>
					)}
				</div>
			</div>

			<div className="card-custom mt-4">
				<h5 className="card-title">Payment Method</h5>
				<div className="payment-tabs mt-2">
					<Controller
						name="paymentType"
						control={control}
						render={({ field }) => (
							<div className="d-flex gap-4 border-bottom pb-2">
								<span
									className={`tab-item ${field.value === 'recurring' ? 'active' : ''}`}
									onClick={() => field.onChange('recurring')}
								>
									Recurring Payment
								</span>

								<span
									className={`tab-item ${field.value === 'one-time' ? 'active' : ''}`}
									onClick={() => field.onChange('one-time')}
								>
									Non-Recurring Payment
								</span>
							</div>
						)}
					/>
				</div>

				<div className="payment-description mt-3">
					<div className="my-3">
						<Controller
							name="paymentMethod"
							control={control}
							render={({ field }) => (
								<InputRadioGroup
									{...field}
									label="Payment Method"
									required
									options={[
										{ label: 'Credit Card', value: 'card' },
										{ label: 'UPI', value: 'upi' },
									]}
									error={errors.paymentMethod?.message}
								/>
							)}
						/>
					</div>

					<div className="mt-3">
						{paymentType === 'recurring' ? (
							<>
								<p className="text-muted small">
									This is a recurring payment. Your credit card / UPI will be charged automatically
									on your next renewal date until you cancel the service.
								</p>

								<p className="small">
									<span className="text-primary fw-semibold">Note:</span> Razorpay Recurring may
									take up to 3 days for the transaction to be completed. You won't be able to make
									any further modification to your licenses for these 3 days.
									<br />
									If you would require your licenses to be modified very often, we suggest you to
									opt for one of the Non Recurring payment options as there is a higher possibility
									of the transaction getting completed instantly.
								</p>
							</>
						) : (
							<p className="text-muted small">
								This is a one-time payment. You will be charged only once and will not be
								automatically renewed.
							</p>
						)}
					</div>
				</div>
			</div>
		</form>
	);
}
